/**
 * resourceCarrier.ts
 * --------------------------------------------------------------------------
 * Cơ chế "vận chuyển" snapshot tài nguyên (giáo án, document, …) qua tab mới
 * khi navigate bằng `window.open` (tab mới KHÔNG nhận được `location.state`).
 *
 * Vấn đề trước đây:
 *  - `handleNavigate(view, { sourceLessonPlan, ...})` với `openInNewTab: true`
 *    chỉ gọi `window.open(path, '_blank')` → toàn bộ object `sourceLessonPlan`
 *    biến mất, tab mới chỉ nhận được query string → các trang creator không
 *    có context để gửi cho AI → AI không sinh được nội dung sát giáo án.
 *
 * Giải pháp:
 *  - Trước khi `window.open`, ghi snapshot vào localStorage với 1 key duy nhất
 *  - Truyền key qua URL: `?carrier=KEY`
 *  - Tab mới đọc localStorage[key], parse, sau đó XÓA key (consume-once)
 *
 * Vì sao localStorage chứ không sessionStorage?
 *  - sessionStorage không chia sẻ giữa tab mới và tab gốc
 *  - localStorage chia sẻ trong cùng origin → phù hợp cross-tab
 *
 * An toàn:
 *  - Auto cleanup các carrier cũ hơn `MAX_AGE_MS` mỗi lần ghi
 *  - Consume-once: xóa ngay khi đọc thành công
 *  - Try/catch toàn bộ để không crash app nếu localStorage bị disable
 */

const KEY_PREFIX = 'eduapp_carrier_';
const MAX_AGE_MS = 5 * 60 * 1000; // 5 phút

export interface ResourceCarrierPayload {
  /** Chủ đề / tiêu đề ngắn để điền sẵn vào input "Tiêu đề" của creator đích */
  initialTopic?: string;
  /** Nội dung mồi (đã serialize ra MARKDOWN) — sẽ vào trường contextContent */
  initialContent?: string;
  /** Yêu cầu chi tiết / mô tả cho AI */
  detailedInstruction?: string;
  /** ID gốc của tài nguyên (nếu đã lưu DB) — fallback để creator tự fetch */
  sourceId?: string;
  /** Tiêu đề tài nguyên gốc — hiển thị trong UI "đã chọn nguồn ABC" */
  sourceTitle?: string;
  /** Chỉ số hoạt động cụ thể (lesson plan step) đang focus, nếu có */
  focusStepIndex?: number;
  /** Snapshot rút gọn của tài nguyên gốc (object JSON) — cho creator nào cần */
  sourceSnapshot?: any;
  /** Loại tài nguyên gốc: lesson_plan / document / quiz … */
  sourceKind?: 'lesson_plan' | 'document' | 'quiz' | 'homework' | 'activity' | 'slide' | 'game' | string;
  /** Payload đặc thù của tính năng (ví dụ: số câu hỏi, độ khó, loại thí nghiệm...) */
  featurePayload?: any;
  /** Timestamp tạo carrier (auto-fill) — dùng để cleanup */
  createdAt?: number;
}

/** Xóa các carrier quá hạn để tránh phình localStorage. */
function cleanupOldCarriers(): void {
  try {
    const now = Date.now();
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(KEY_PREFIX)) continue;
      const v = localStorage.getItem(k);
      if (!v) {
        toRemove.push(k);
        continue;
      }
      try {
        const parsed = JSON.parse(v) as ResourceCarrierPayload;
        if (typeof parsed?.createdAt !== 'number' || now - parsed.createdAt > MAX_AGE_MS) {
          toRemove.push(k);
        }
      } catch {
        toRemove.push(k);
      }
    }
    toRemove.forEach(k => {
      try { localStorage.removeItem(k); } catch {}
    });
  } catch {
    // localStorage bị block / quota: bỏ qua
  }
}

/**
 * Ghi snapshot vào carrier, trả về key (hoặc null nếu lỗi).
 * Caller dùng key này nối vào URL: `?carrier=KEY`
 */
export function writeResourceCarrier(payload: ResourceCarrierPayload): string | null {
  if (!payload || typeof payload !== 'object') return null;
  try {
    cleanupOldCarriers();
    const key = `${KEY_PREFIX}${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const enriched: ResourceCarrierPayload = { ...payload, createdAt: Date.now() };
    localStorage.setItem(key, JSON.stringify(enriched));
    return key;
  } catch {
    return null;
  }
}

/**
 * Đọc carrier theo key. Tự động XÓA key sau khi đọc thành công (consume-once)
 * để không bị áp dụng lại nếu user reload trang.
 */
export function readResourceCarrier(key: string | null | undefined): ResourceCarrierPayload | null {
  if (!key || typeof key !== 'string') return null;
  if (!key.startsWith(KEY_PREFIX)) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try { localStorage.removeItem(key); } catch {}
    const parsed = JSON.parse(raw) as ResourceCarrierPayload;
    if (!parsed || typeof parsed !== 'object') return null;
    // Hết hạn: bỏ qua
    if (typeof parsed.createdAt === 'number' && Date.now() - parsed.createdAt > MAX_AGE_MS) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Tiện ích: lấy `carrier` key từ URLSearchParams. */
export function extractCarrierKey(searchParams: URLSearchParams): string | null {
  return searchParams.get('carrier');
}
